// import axios from 'axios';
// import store from '../redux/store'; // Import your Redux store
// import { logout } from '../redux/actions/authActions'; // Import your logout action
// import { createBrowserHistory } from 'history';

// const axiosInstance = axios.create({
//   baseURL: process.env.REACT_APP_API_URL, // Use the environment variable
// });

// // Create a history object
// const history = createBrowserHistory();

// // Add a response interceptor
// axiosInstance.interceptors.response.use(
//   (response) => response,
//   (error) => {
//     if (error.response && error.response.status === 401) {
//       const errorMessage = error.response.data.error;
//       if (errorMessage === 'token_expired') {
//         // Dispatch logout action
//         store.dispatch(logout());
//         // Redirect to login page
//         history.push('/login');
//         window.location.reload(); // Reload to ensure the navigation occurs
//       }
//     }
//     return Promise.reject(error);
//   }
// );

// export default axiosInstance;
import axios from 'axios';

// Create an axios instance with performance optimizations
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001',
  timeout: 0, // 10 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to include the token and optimize requests
axiosInstance.interceptors.request.use(
  (config) => {
    console.log('API URL:', process.env.REACT_APP_API_URL);
    const token = localStorage.getItem('token'); // Replace with how you're storing the token
    
    // console.log('Token:', token); // Debugging log
    if (token) {
      config.headers['x-auth-token'] = token; // Set token in x-auth-token header
    }
    // console.log('Config:', config); // Debugging
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Helper function to cleanup old cache entries
// IMPORTANT: This ONLY affects sessionStorage, NOT localStorage
// User data in localStorage (token, drafts, preferences) is SAFE
const cleanupOldCache = () => {
  const CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes
  const MAX_CACHE_KEYS = 20000; // Max number of cached items
  const now = Date.now();
  
  try {
    const keysToRemove = [];
    
    // Check all sessionStorage keys (NOT localStorage)
    // This safely removes ONLY API cache entries, preserving user data
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      
      // Only remove entries that start with 'api_cache_' prefix
      if (key && key.startsWith('api_cache_')) {
        try {
          const cached = JSON.parse(sessionStorage.getItem(key));
          
          // Remove if expired
          if (now - cached.timestamp > CACHE_EXPIRY) {
            keysToRemove.push(key);
          }
        } catch (e) {
          // Remove invalid entries
          keysToRemove.push(key);
        }
      }
    }
    
    // Remove expired/invalid keys
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    
    // If still too many entries, remove oldest ones
    const allCacheKeys = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('api_cache_')) {
        try {
          const cached = JSON.parse(sessionStorage.getItem(key));
          allCacheKeys.push({ key, timestamp: cached.timestamp });
        } catch (e) {
          sessionStorage.removeItem(key);
        }
      }
    }
    
    if (allCacheKeys.length > MAX_CACHE_KEYS) {
      // Sort by timestamp and remove oldest
      allCacheKeys.sort((a, b) => a.timestamp - b.timestamp);
      const toRemove = allCacheKeys.slice(0, allCacheKeys.length - MAX_CACHE_KEYS);
      toRemove.forEach(item => sessionStorage.removeItem(item.key));
      console.log(`Cache cleanup: removed ${toRemove.length} old entries`);
    }
  } catch (error) {
    console.error('Cache cleanup error:', error);
  }
};

// Add response interceptor for better error handling
axiosInstance.interceptors.response.use(
  (response) => {
    // Cache successful responses with size limits and expiration
    if (response.config.method === 'get') {
      const cacheKey = `api_cache_${response.config.url}`;
      
      try {
        // Get the data as string to check size
        const dataString = JSON.stringify({
          data: response.data,
          timestamp: Date.now()
        });
        
        // Skip caching if data is too large (>500KB)
        if (dataString.length > 1000 * 1024) {
          console.warn(`Response too large to cache: ${response.config.url}`);
          return response;
        }
        
        // Clean old cache entries before adding new one
        cleanupOldCache();
        
        // Store in sessionStorage
        sessionStorage.setItem(cacheKey, dataString);
      } catch (error) {
        // If storage fails (quota exceeded), cleanup and skip caching
        if (error.name === 'QuotaExceededError' || error.name === 'QUOTA_EXCEEDED_ERR') {
          console.warn('Storage quota exceeded, cleaning cache');
          // Clean all API cache entries from sessionStorage ONLY
          // User data in localStorage remains untouched
          for (let i = sessionStorage.length - 1; i >= 0; i--) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith('api_cache_')) {
              sessionStorage.removeItem(key);
            }
          }
        } else {
          console.error('Cache error:', error);
        }
      }
    }
    return response;
  },
  (error) => {
    // Handle network errors gracefully without causing infinite loops
    if (error.code === "ERR_NETWORK" || error.message.includes('CORS')) {
      console.log('CORS or network error detected:', error.message);
      // Don't reload the page automatically - let the component handle the error
      // This prevents infinite refresh loops
    }
    
    // Handle authentication errors
    if (error.response && error.response.status === 401) {
      console.log('Authentication error detected');
      // Clear invalid token
      localStorage.removeItem('token');
      // Don't reload - let the app handle the redirect
    }
    
    return Promise.reject(error);
  }
);

export default axiosInstance;
